import { detectDirection } from '../core/direction'
import { DIRECTIONAL_ICONS } from './icon-manifest'

export interface IconOptions {
  size?: number
  className?: string
  /** Force mirroring on/off; defaults to auto-detection from the manifest + document direction */
  mirror?: boolean
}

let spriteBase = ''
const inlineCache = new Map<string, Promise<string>>()

/** Called once by core/init with the published base path, e.g. "/shared/v1" */
export function configureIconBase(base: string): void {
  spriteBase = base
}

function resolveMirror(name: string, mirror?: boolean): boolean {
  if (mirror !== undefined)
    return mirror
  return detectDirection() === 'rtl' && DIRECTIONAL_ICONS.has(name)
}

function applyIconAttrs(svg: SVGSVGElement, size: number, className: string | undefined, mirror: boolean): void {
  svg.setAttribute('width', String(size))
  svg.setAttribute('height', String(size))
  svg.setAttribute('aria-hidden', 'true')
  svg.setAttribute('class', ['sn-icon', className, mirror ? 'sn-icon--mirrored' : ''].filter(Boolean).join(' '))
}

/**
 * Creates an <svg><use> element referencing the shared icon sprite
 * (Section 10.3). This is the cheap, default way to render an icon: one
 * network request for the whole sprite, then instant reuse for every
 * icon on the page. Direction-sensitive icons (arrows, chevrons,
 * undo/redo, ...) are automatically mirrored when the page is RTL,
 * unless `mirror` is explicitly set.
 */
export function icon(name: string, options: IconOptions = {}): SVGSVGElement {
  const size = options.size ?? 24
  const mirror = resolveMirror(name, options.mirror)

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  applyIconAttrs(svg, size, options.className, mirror)

  const use = document.createElementNS('http://www.w3.org/2000/svg', 'use')
  use.setAttributeNS('http://www.w3.org/1999/xlink', 'href', `${spriteBase}/icons/sprite.svg#${name}`)
  svg.appendChild(use)

  return svg
}

/**
 * Fetches and inlines a single icon's real SVG markup (Section 10.2.1),
 * instead of referencing the shared sprite. Use this when you need a
 * fully self-contained SVG — e.g. to hand off to something that can't
 * resolve <use href>, to inject into a Shadow DOM, or to only ever load
 * the handful of icons a page actually uses. Requests are cached and
 * de-duplicated per icon name.
 */
export async function iconInline(name: string, options: IconOptions = {}): Promise<SVGSVGElement> {
  const size = options.size ?? 24
  const mirror = resolveMirror(name, options.mirror)

  if (!inlineCache.has(name)) {
    inlineCache.set(
      name,
      fetch(`${spriteBase}/icons/ui/${name}.svg`).then((res) => {
        if (!res.ok)
          throw new Error(`SafheNet.iconInline: icon "${name}" not found (${res.status})`)
        return res.text()
      }),
    )
  }

  const markup = await inlineCache.get(name)!
  const parsed = new DOMParser().parseFromString(markup, 'image/svg+xml').documentElement as unknown as SVGSVGElement
  applyIconAttrs(parsed, size, options.className, mirror)
  return parsed
}
